import React from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
//import "./comparisonLineChart.css";

interface DataPoint {
  subject: string;
  [key: string]: string | number | undefined;
}

interface DataSet {
  dataKey: string;
  strokeColor: string;
  strokeWidth?: number;
}

interface ComparisonLineChartProps {
  title: string;
  data: DataPoint[];
  datasets: DataSet[];
  colors: string[];
  legendLabels: string[];
  className?: string;
}

const ComparisonLineChart: React.FC<ComparisonLineChartProps> = ({
  title,
  data,
  datasets,
  colors,
  legendLabels,
  className = "",
}) => {
  // Keeping your color logic for the decorative SVGs
  const darkenColor = (color: string): string => {
    if (color.startsWith("var(")) {
      if (color.includes("--color-light-gray")) return "#d0cfcf";
      if (color.includes("--color-gray")) return "#b8b8b8";
      if (color.includes("--color-dark-blue")) return "#1e3c5a";
      if (color.includes("--color-blue")) return "#3d6b9c";
      if (color.includes("--color-light-blue")) return "#2a8fa6";
      if (color.includes("--color-teal")) return "#026d7a";
      if (color.includes("--color-green")) return "#1a8b60";
      if (color.includes("--color-orange")) return "#c5471a";
      if (color.includes("--color-yellow")) return "#b8750a";
      return "#999999";
    }
    return color;
  };

  const shapeColor = darkenColor("var(--color-light-gray)");

  return (
    <div className={`comparisonRadarChart-container ${className}`}>
      {/* Decorative Top Left SVG */}
      <svg
        className="comparisonRadarChart-svg-top-left"
        width="160"
        height="40"
        viewBox="0 0 111 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M65.0082 15.7865C100.199 12.0577 108.741 3.83731 110.135 0.914062L0.00927734 0.914071L0.00927896 16.9908C8.37324 18.1601 30.8655 19.4042 65.0082 15.7865Z"
          fill={shapeColor}
        />
      </svg>

      {/* Decorative Bottom Right SVG */}
      <svg
        className="comparisonRadarChart-svg-bottom-right"
        width="280"
        height="70"
        viewBox="0 0 186 35"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <g clipPath="url(#clip0_54620_1532)">
          <path
            d="M102.58 15.493C38.5252 15.493 7.8007 28.1851 0.445312 34.5312H185.801V0.685547C184.75 5.62137 166.635 15.493 102.58 15.493Z"
            fill={shapeColor}
          />
        </g>
        <defs>
          <clipPath id="clip0_54620_1532">
            <path
              d="M0.445312 0.685547H185.801V24.5312C185.801 30.0541 181.324 34.5312 175.801 34.5312H0.445312V0.685547Z"
              fill="white"
            />
          </clipPath>
        </defs>
      </svg>

      {title && (
        <div className="comparisonRadarChart-header">
          <span className="comparisonRadarChart-subtitle">Comparison chart</span>
          <h3 className="comparisonRadarChart-title">{title.toUpperCase()}</h3>
        </div>
      )}

      <div className="comparisonRadarChart-content" style={{ display: "block" }}>
        {/* Custom Legend Section */}
        {/* <div className="comparisonRadarChart-legend" style={{ marginBottom: "2rem" }}>
          {colors.map((color, index) => (
            <div key={index} className="comparisonRadarChart-legend-item">
              <div
                className="comparisonRadarChart-legend-circle"
                style={{ backgroundColor: color }}
              ></div>
              <span className="comparisonRadarChart-legend-label">
                {legendLabels[index]}
              </span>
            </div>
          ))}
        </div> */}

        {/* Line Chart Section */}
        <div style={{ width: "100%", height: "340px" }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={data}
              margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid 
                strokeDasharray="3 3" 
                vertical={false} 
                stroke="var(--color-light-gray)" 
              />
              <XAxis 
                dataKey="subject" 
                tick={{ fill: "var(--color-dark-blue)", fontSize: "0.75rem" }}
                axisLine={false}
                tickLine={true}
              />
              <YAxis 
                tick={{ fill: "var(--color-dark-blue)", fontSize: "0.75rem" }}
                axisLine={true}
                tickLine={true}
              />
              <Tooltip 
                contentStyle={{ 
                  borderRadius: "8px", 
                  border: "none", 
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)" 
                }} 
              />
              {datasets.map((dataset, index) => (
                <Line
                  key={index}
                  type="monotone"
                  dataKey={dataset.dataKey}
                  stroke={dataset.strokeColor}
                  strokeWidth={dataset.strokeWidth || 3}
                  dot={{ r: 4, fill: dataset.strokeColor }}
                  activeDot={{ r: 6 }}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default ComparisonLineChart;
